// translations/pages/kyveil/es.js

export const kyveilPageES = {
  "seo": {
    "title": "Anonimización de PII con Kyveil | pAIoneer Labs GmbH",
    "description": "Kyveil detecta y anonimiza los datos personales de forma local, en sus propios sistemas. Aplicación de escritorio o API REST para una anonimización de PII conforme al RGPD.",
    "keywords": "Kyveil, anonimización de PII, anonimizar datos personales, protección de datos, privacidad, RGPD, GDPR, Microsoft Presidio, spaCy, flair, procesamiento local de datos",
    "ogTitle": "Kyveil: anonimización profesional de PII con IA y NLP",
    "ogDescription": "Detección y anonimización automáticas de datos personales como aplicación de escritorio o API REST en su propia infraestructura.",
    "twitterTitle": "Anonimización de PII con Kyveil",
    "twitterDescription": "Detección y anonimización locales de PII para documentos sensibles.",
    "url": "https://paioneer-labs.eu/kyveil",
    "structuredData": {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Kyveil",
      "applicationCategory": "SecurityApplication",
      "operatingSystem": "Windows, macOS, Linux, Server",
      "description": "Software para la detección y anonimización locales de datos personales con IA y NLP.",
      "provider": {
        "@type": "Organization",
        "name": "pAIoneer Labs GmbH",
        "url": "https://paioneer-labs.eu"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "url": "https://paioneer-labs.eu/kyveil"
      }
    }
  },
  "hero": {
    "eyebrow": "Protección de datos y anonimización",
    "title": "Kyveil",
    "subtitle": "Anonimización local antes de la IA, las pruebas y la cesión a terceros.",
    "description": "Kyveil es una capa de anonimización local para documentos y flujos de trabajo de IA. El software detecta datos personales en archivos Word, Excel, PDF y de texto, los reemplaza o enmascara y muestra una vista previa antes de que el contenido pase a chats de IA, pruebas, análisis o uso compartido externo. Kyveil se desarrolla y evoluciona de forma continua [impulsado por IDD](idd) (Intent-Driven Development) y automatizado con [Veridion](veridion) por nuestros colaboradores de IA con personas en el bucle.",
    "platforms": "Aplicación de escritorio, API REST y AnonChat",
    "proofPoints": [
      "Los documentos permanecen en local, en sus sistemas.",
      "Los datos personales se sustituyen o se enmascaran.",
      "Una vista previa muestra el contenido anonimizado antes de compartirlo o de usarlo en un chat de IA.",
      "Word, Excel, PDF y texto son los flujos de trabajo documentales centrales."
    ]
  },
  "dataSovereignty": {
    "title": "Sus datos. Sus sistemas. Su control.",
    "subtitle": "Soberanía total sobre los datos gracias al procesamiento local.",
    "items": [
      {
        "title": "No requiere nube",
        "description": "Todo el procesamiento se realiza de forma local en su infraestructura. Sus archivos nunca salen de sus sistemas. Sin dependencias de la nube ni transmisión externa de datos."
      },
      {
        "title": "Edge computing",
        "description": "La aplicación de escritorio funciona de forma totalmente autónoma en los puestos de trabajo. La API se ejecuta en sus propios servidores. Ambas variantes procesan todos los datos de forma local, dentro de su perímetro de seguridad."
      },
      {
        "title": "Autonomía de los datos",
        "description": "Control total sobre sus flujos de datos. Sin dependencia de un único proveedor. Sin costes recurrentes de nube. Su información sensible permanece exclusivamente bajo su control."
      },
      {
        "title": "Hardware estándar",
        "description": "No requiere GPU. Funciona en ordenadores de oficina convencionales y en servidores estándar. No es necesario invertir en hardware especializado."
      }
    ]
  },
  "features": {
    "title": "Funciones principales",
    "subtitle": "Todo lo necesario para una detección y anonimización controladas de PII en los procesos de la empresa.",
    "items": [
      {
        "title": "Detección de PII basada en IA",
        "description": "Reconocimiento automático con Microsoft Presidio SDK, spaCy de Explosion AI y flair de la Humboldt-Universität zu Berlin.",
        "tone": "primary"
      },
      {
        "title": "Modo Replace",
        "description": "Sustituye la PII detectada por marcadores genéricos como [NAME], [EMAIL] y [ADDRESS] para escenarios de cumplimiento normativo.",
        "tone": "info"
      },
      {
        "title": "Modo Fake",
        "description": "Sustituye la PII detectada por valores sustitutivos verosímiles para entornos de prueba y desarrollo.",
        "tone": "warning"
      },
      {
        "title": "Varios formatos de documento",
        "description": "Procesa archivos de Microsoft Word, Excel, OpenDocument y PDF, así como texto sin formato.",
        "tone": "success"
      },
      {
        "title": "Compatibilidad multilingüe",
        "description": "Compatibilidad integrada con alemán, inglés, francés, italiano y español mediante modelos NLP específicos de cada idioma.",
        "tone": "secondary"
      },
      {
        "title": "Detección configurable",
        "description": "Defina qué entidades de PII deben detectarse: nombres, direcciones, correos electrónicos, números de teléfono, cuentas bancarias, números de identificación fiscal y más.",
        "tone": "primary"
      },
      {
        "title": "Extensibilidad",
        "description": "Interfaces para lógica propia de preprocesamiento y posprocesamiento que se integra en sus flujos de trabajo específicos.",
        "tone": "info"
      },
      {
        "title": "Optimizado para CPU",
        "description": "La eficiente arquitectura de los modelos funciona en hardware de oficina estándar sin necesidad de GPU.",
        "tone": "warning"
      }
    ]
  },
  "technology": {
    "title": "Stack tecnológico",
    "subtitle": "Frameworks de nivel empresarial e investigación contrastada en NLP.",
    "items": [
      {
        "title": "Microsoft Presidio SDK",
        "description": "Microsoft Data Protection and De-identification SDK. Framework de referencia en el sector para la detección y anonimización de PII, con reconocedores de entidades integrados.",
        "tone": "primary"
      },
      {
        "title": "spaCy",
        "subtitle": "Desarrollado por Explosion AI",
        "description": "Industrial-Strength Natural Language Processing. Biblioteca de NLP lista para producción con modelos estadísticos preentrenados para el reconocimiento de entidades nombradas.",
        "tone": "info"
      },
      {
        "title": "flair",
        "subtitle": "Desarrollado por la Humboldt-Universität zu Berlin",
        "description": "Framework de NLP de última generación con embeddings contextuales de cadenas. Modelos avanzados de redes neuronales para una mayor precisión en la detección de entidades.",
        "tone": "secondary"
      }
    ],
    "integration": "Pipeline de IA integrado",
    "integrationDesc": "Detección combinada con Presidio como capa de orquestación y modelos de spaCy y flair para una detección óptima en varios idiomas y tipos de entidad."
  },
  "technicalDetails": {
    "title": "Capacidades técnicas",
    "subtitle": "Detección configurable para documentos empresariales europeos.",
    "items": [
      {
        "title": "Idiomas compatibles",
        "description": "Modelos NLP preentrenados para alemán, inglés, francés, italiano y español. Reconocimiento de entidades específico de cada idioma para una detección precisa de PII en contextos empresariales europeos.",
        "tone": "primary"
      },
      {
        "title": "Entidades de PII configurables",
        "description": "Seleccione qué tipos de datos personales deben detectarse y anonimizarse: nombres de personas, direcciones de correo electrónico, números de teléfono, direcciones postales, cuentas bancarias IBAN, números de tarjetas de crédito, números de la seguridad social, números de identificación fiscal, números de pasaporte y más.",
        "tone": "info"
      },
      {
        "title": "Modos de anonimización",
        "description": "El modo Replace sustituye la PII por marcadores genéricos para la documentación de cumplimiento normativo. El modo Fake utiliza valores sustitutivos verosímiles para entornos de prueba y conserva la estructura del documento.",
        "tone": "warning"
      },
      {
        "title": "Preprocesamiento y posprocesamiento",
        "description": "Implemente lógica propia antes y después de la anonimización. Añada validación de documentos, conversión de formatos, controles de calidad, registro de auditoría o integración con sistemas posteriores a través de las interfaces proporcionadas.",
        "tone": "secondary"
      }
    ]
  },
  "anonChat": {
    "title": "AnonChat amplía Kyveil con un chat web de IA",
    "subtitle": "La nueva aplicación utiliza el mismo enfoque de anonimización antes del chat propiamente dicho con sistemas de IA externos o internos.",
    "eyebrow": "Nueva aplicación",
    "name": "AnonChat",
    "description": "AnonChat es un chat web de IA en el que los documentos de Word y Excel pueden anonimizarse antes de utilizarlos con ChatGPT, Claude, Gemini o LLM internos. Una vista previa muestra el texto anonimizado antes de que los contenidos pasen al chat.",
    "statement": "Así, la decisión queda en manos del usuario: revisar, aprobar y solo entonces chatear.",
    "steps": [
      {
        "title": "Cargar el documento",
        "description": "Los archivos de Word o Excel se preparan para la anonimización antes de entrar en el flujo de trabajo de IA.",
        "tone": "primary"
      },
      {
        "title": "Anonimizar la PII",
        "description": "Kyveil detecta los datos personales y los sustituye o enmascara según las reglas elegidas.",
        "tone": "info"
      },
      {
        "title": "Revisar la vista previa",
        "description": "El texto anonimizado se muestra antes de que los contenidos se entreguen al chat.",
        "tone": "warning"
      },
      {
        "title": "Usar el chat de IA",
        "description": "Solo tras la revisión se utilizan los contenidos anonimizados con LLM externos o internos.",
        "tone": "success"
      }
    ]
  },
  "deployment": {
    "title": "Opciones de despliegue",
    "subtitle": "Elija la solución adecuada para su caso de uso.",
    "options": [
      {
        "title": "Aplicación de escritorio",
        "subtitle": "Aplicación autónoma para puestos de trabajo individuales",
        "description": "La versión de escritorio funciona de forma totalmente independiente en Windows, macOS o Linux. Es ideal para usuarios que necesitan anonimizar documentos directamente en su puesto de trabajo, sin dependencias de red.",
        "features": [
          "Funcionamiento 100 % sin conexión: no se requiere acceso a internet",
          "Los archivos nunca salen del equipo local",
          "Procesamiento por lotes de varios archivos",
          "Interfaz gráfica de usuario de manejo sencillo",
          "Funciona en ordenadores de oficina estándar sin GPU",
          "Licencia única, sin modelo de suscripción"
        ]
      },
      {
        "title": "API REST",
        "subtitle": "Despliegue en servidor para integración y escalado",
        "description": "La versión API se ejecuta en sus propios servidores o en una infraestructura de nube privada. Es ideal para integrar la anonimización en sistemas y flujos de trabajo existentes con grandes volúmenes de procesamiento.",
        "features": [
          "Despliegue en infraestructura propia: en sus instalaciones o en nube privada",
          "Escalado horizontal para altas exigencias de rendimiento",
          "API RESTful con documentación completa",
          "El mismo motor de anonimización que la versión de escritorio",
          "Ejemplos de integración para las plataformas más habituales",
          "Posibilidad de adaptar el preprocesamiento y el posprocesamiento"
        ]
      },
      {
        "title": "AnonChat",
        "subtitle": "Chat web de IA con anonimización previa",
        "description": "AnonChat sitúa Kyveil delante del chat de IA propiamente dicho. Los documentos de Word y Excel se anonimizan, se revisa una vista previa y solo después pasan los contenidos a ChatGPT, Claude, Gemini o LLM internos.",
        "features": [
          "Anonimización antes del chat en lugar de un control a posteriori",
          "Vista previa del texto anonimizado antes de la entrega",
          "Pensado para preguntas a sistemas de IA basadas en documentos",
          "Reduce el riesgo de enviar datos personales a modelos externos",
          "Puede conectarse a LLM internos o a entornos de chat corporativos",
          "Utiliza el mismo enfoque de anonimización que la aplicación de escritorio y la API REST"
        ]
      }
    ],
    "bothNote": "La aplicación de escritorio, la API REST y AnonChat están concebidos para anonimizar de forma controlada los contenidos sensibles antes de su procesamiento posterior."
  },
  "useCases": {
    "title": "Casos de uso",
    "subtitle": "Desde el cumplimiento del RGPD hasta datos de desarrollo seguros.",
    "items": [
      {
        "title": "Cumplimiento del RGPD",
        "description": "Anonimice los datos personales antes de compartir documentos con terceros o con departamentos internos. Cumpla los requisitos de protección de datos para datos de prueba, conjuntos de datos de análisis y documentación.",
        "tone": "primary"
      },
      {
        "title": "Preparación de datos de prueba",
        "description": "Prepare conjuntos de datos de prueba realistas para que los desarrolladores puedan trabajar con datos similares a los de producción sin exponer información personal real.",
        "tone": "info"
      },
      {
        "title": "Procesamiento de documentos de RR. HH.",
        "description": "Anonimice candidaturas, expedientes de personal y documentación de RR. HH. para evaluaciones imparciales, auditorías de cumplimiento o análisis de datos, preservando la privacidad.",
        "tone": "warning"
      },
      {
        "title": "Análisis de datos",
        "description": "Haga posible el análisis de documentos con PII anonimizando antes la información sensible. Comparta conjuntos de datos entre equipos sin reservas en materia de protección de datos.",
        "tone": "success"
      },
      {
        "title": "Archivado de documentos",
        "description": "Prepare documentos para el archivado a largo plazo eliminando o anonimizando la PII conforme a las políticas de conservación y a la normativa de protección de datos.",
        "tone": "secondary"
      },
      {
        "title": "Intercambio seguro",
        "description": "Comparta documentos con socios externos, contratistas u otros departamentos una vez eliminada automáticamente la información personal sensible.",
        "tone": "error"
      }
    ]
  },
  "releases": {
    "title": "Variantes y soporte",
    "subtitle": "Actualizaciones, ampliaciones y soporte para el uso en producción.",
    "items": [
      {
        "title": "Escritorio, API y AnonChat",
        "description": "Kyveil está disponible como aplicación de escritorio y como API REST para integraciones. AnonChat es el chat web con anonimización previa de los documentos.",
        "tone": "info"
      },
      {
        "title": "Soporte",
        "description": "Para el uso en empresas, ofrecemos soporte técnico, ayuda con la implementación y adaptaciones.",
        "tone": "success"
      }
    ]
  },
  "cta": {
    "title": "Empiece con Kyveil",
    "subtitle": "Proteja los documentos sensibles antes de que lleguen a la IA, a las pruebas, a los análisis o a terceros.",
    "desktop": "Solicitar la aplicación de escritorio",
    "desktopSubject": "Kyveil Desktop",
    "apiContact": "Contacto para el despliegue de la API REST",
    "contact": "Para solicitar una demostración o un presupuesto, escríbanos.",
    "email": "info@paioneer.eu",
    "apiSubject": "Despliegue de la API REST de Kyveil",
    "contactSubject": "Demostración o presupuesto de Kyveil"
  }
};
